import { Injectable, inject } from '@angular/core';

import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, from, of } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class NexRefreshAuthTokenService {
  private readonly oauth = inject(OAuthService);
  private refreshInFlight$?: Observable<boolean>;

  /** Expose whether a refresh token seems present/usable */
  public hasRefreshToken(): boolean {
    try {
      const token = this.oauth.getRefreshToken();

      return token !== null && token !== undefined && token.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Ensure we have a valid access token:
   *  - refresh available: performs a single-flight refresh
   *  - If refresh fails: resolves false and emits on refreshFailed$
   */
  public ensureValidAccessToken$(): Observable<boolean> {
    if (!this.hasRefreshToken()) {
      return of(false);
    }

    return this.refreshAccessToken$();
  }

  /** Get the current access token */
  public getAccessToken() {
    return this.oauth.getAccessToken();
  }

  /** Perform a single-flight refresh across all concurrent 401s */
  private refreshAccessToken$(): Observable<boolean> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = from(this.oauth.refreshToken()).pipe(
        switchMap(() => of(this.oauth.hasValidAccessToken())),
        catchError(() => of(false)),
        finalize(() => {
          // allow a new refresh on next demand
          this.refreshInFlight$ = undefined;
        }),
        // Share the same refresh result with all waiters
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    // Late subscribers will immediately get shared result
    return this.refreshInFlight$.pipe(take(1));
  }
}
